/*
 * PDP floating video + pop-up - behaviour only.
 *
 * Markup is rendered by snippets/pdp-floating-video.liquid, styling lives in
 * assets/custom.css. What is left here is what a server cannot do: the delayed
 * reveal, dragging the player around, and opening/closing the pop-up.
 *
 * Ported from the Variant experience deployed as exp-EXP9AA81A29. The
 * analytics ids below are the ones the shipped script reported under and are
 * kept so existing dashboards keep resolving.
 */
(function () {
  'use strict';

  var EXPERIMENT_ID = 'EXP6B059AA3';
  var VARIATION_ID = '3';

  var REVEAL_DELAY = 2000;
  var DESKTOP_BREAKPOINT = 1000;
  var DRAG_THRESHOLD = 6;
  var EDGE_MARGIN = 16;

  function track(extra) {
    try {
      if (typeof Variant === 'undefined' || !Variant.publishCustomEvent) return;
      var payload = { experiment_id: EXPERIMENT_ID, variation_id: VARIATION_ID };
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) payload[key] = extra[key];
      }
      Variant.publishCustomEvent('influenced-revenue', payload);
    } catch (e) {
      /* never break the page for analytics */
    }
  }

  var PdpFloatingVideo = class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.player = this.querySelector('[data-pdp-floating-player]');
      this.video = this.querySelector('[data-pdp-floating-video]');
      this.dismissButton = this.querySelector('[data-pdp-floating-dismiss]');
      this.expandButton = this.querySelector('[data-pdp-floating-expand]');
      this.overlay = this.querySelector('[data-pdp-video-overlay]');
      this.popupVideo = this.querySelector('[data-pdp-popup-video]');
      this.popupClose = this.querySelector('[data-pdp-popup-close]');

      if (!this.player || !this.video || !this.overlay) return;

      this.popupOpen = false;
      this.hidden_ = false;
      this.dragging = false;
      this.wasDragged = false;

      this.bindEvents();

      this.revealTimer = setTimeout(this.reveal.bind(this), REVEAL_DELAY);
    }

    disconnectedCallback() {
      clearTimeout(this.revealTimer);
      this.unbindDocumentEvents();
      if (this.popupOpen) document.body.style.overflow = '';
    }

    bindEvents() {
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.player.addEventListener('touchstart', this.onPointerDown, { passive: true });
      this.player.addEventListener('mousedown', this.onPointerDown);
      document.addEventListener('touchmove', this.onPointerMove, { passive: false });
      document.addEventListener('mousemove', this.onPointerMove);
      document.addEventListener('touchend', this.onPointerUp);
      document.addEventListener('mouseup', this.onPointerUp);
      document.addEventListener('keydown', this.onKeyDown);

      this.player.addEventListener(
        'click',
        function (event) {
          if (this.wasDragged) return;
          if (this.isButton(event.target)) return;
          track({});
        }.bind(this)
      );

      this.expandButton.addEventListener(
        'click',
        function (event) {
          event.stopPropagation();
          this.openPopup();
          track({ action: 'expand_button_click' });
        }.bind(this)
      );

      this.dismissButton.addEventListener(
        'click',
        function (event) {
          event.stopPropagation();
          track({ action: 'close_button_click' });
          this.fadeOutAndHide();
        }.bind(this)
      );

      // Playing through once is the cue that the widget is done.
      this.video.addEventListener('ended', this.fadeOutAndHide.bind(this));

      this.popupClose.addEventListener(
        'click',
        function (event) {
          event.stopPropagation();
          this.closePopup();
        }.bind(this)
      );

      // Only the dimmed backdrop closes; clicks on the video itself do not.
      this.overlay.addEventListener(
        'click',
        function (event) {
          if (event.target === this.overlay) this.closePopup();
        }.bind(this)
      );
    }

    unbindDocumentEvents() {
      document.removeEventListener('touchmove', this.onPointerMove);
      document.removeEventListener('mousemove', this.onPointerMove);
      document.removeEventListener('touchend', this.onPointerUp);
      document.removeEventListener('mouseup', this.onPointerUp);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    isButton(target) {
      return (
        target === this.dismissButton ||
        this.dismissButton.contains(target) ||
        target === this.expandButton ||
        this.expandButton.contains(target)
      );
    }

    // -----------------------------------------------------------------------
    // Reveal
    // -----------------------------------------------------------------------
    reveal() {
      if (this.hidden_) return;

      this.positionForDesktop();
      this.player.classList.add('is-visible');

      this.video.muted = true;
      this.video.preload = 'auto';
      this.video.load();

      var play = function () {
        this.video.muted = true;
        var promise = this.video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () {
            /* autoplay may be blocked; the poster stays visible */
          });
        }
      }.bind(this);

      if (this.video.readyState >= 3) {
        play();
      } else {
        this.video.addEventListener('canplay', play, { once: true });
        setTimeout(play, 1500);
      }
    }

    /**
     * On wide viewports the player is pinned to the top-left corner of the
     * product gallery instead of the bottom-left of the screen. It stays
     * position:fixed, so it remains on screen while the page scrolls.
     */
    positionForDesktop() {
      if (window.innerWidth < DESKTOP_BREAKPOINT) return;

      var firstMedia = document.querySelector('product-gallery.product-gallery .product-gallery__media');
      if (!firstMedia) return;

      var rect = firstMedia.getBoundingClientRect();
      this.player.style.bottom = 'auto';
      this.player.style.right = 'auto';
      this.player.style.top = rect.top + 12 + 'px';
      this.player.style.left = rect.left + 12 + 'px';
    }

    fadeOutAndHide() {
      if (this.hidden_) return;
      this.hidden_ = true;

      this.player.classList.add('is-hiding');

      setTimeout(
        function () {
          this.player.classList.add('is-gone');
          this.video.pause();
        }.bind(this),
        1400
      );
    }

    // -----------------------------------------------------------------------
    // Pop-up
    // -----------------------------------------------------------------------
    openPopup() {
      if (this.popupOpen) return;
      this.popupOpen = true;

      document.body.style.overflow = 'hidden';

      // Carry the playback position over so the pop-up feels like the same clip.
      try {
        this.popupVideo.currentTime = this.video.currentTime || 0;
      } catch (e) {
        /* ignore - the video may not be seekable yet */
      }

      this.popupVideo.muted = false;
      if (this.popupVideo.readyState < 2) {
        this.popupVideo.preload = 'auto';
        this.popupVideo.load();
      }

      // Force a reflow so the opening transition always runs.
      void this.overlay.offsetWidth;
      this.overlay.classList.add('is-open');

      var play = function () {
        var promise = this.popupVideo.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () {
            /* ignore */
          });
        }
      }.bind(this);

      if (this.popupVideo.readyState >= 2) {
        play();
      } else {
        this.popupVideo.addEventListener('canplay', play, { once: true });
        setTimeout(play, 600);
      }
    }

    closePopup() {
      if (!this.popupOpen) return;
      this.popupOpen = false;

      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';

      setTimeout(
        function () {
          if (!this.popupOpen) this.popupVideo.pause();
        }.bind(this),
        460
      );
    }

    onKeyDown(event) {
      if (this.popupOpen && (event.key === 'Escape' || event.keyCode === 27)) this.closePopup();
    }

    // -----------------------------------------------------------------------
    // Drag
    // -----------------------------------------------------------------------
    onPointerDown(event) {
      if (this.isButton(event.target)) return;

      var point = event.touches ? event.touches[0] : event;
      var rect = this.player.getBoundingClientRect();

      this.startX = point.clientX;
      this.startY = point.clientY;
      this.initialLeft = rect.left;
      this.initialTop = rect.top;
      this.dragging = true;
      this.wasDragged = false;

      // Switch from the bottom/left anchoring to explicit coordinates.
      this.player.style.bottom = 'auto';
      this.player.style.right = 'auto';
      this.player.style.left = this.initialLeft + 'px';
      this.player.style.top = this.initialTop + 'px';
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      var point = event.touches ? event.touches[0] : event;
      var dx = point.clientX - this.startX;
      var dy = point.clientY - this.startY;

      if (!this.wasDragged && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;

      this.wasDragged = true;
      this.player.classList.add('is-dragging');

      var rect = this.player.getBoundingClientRect();
      var maxLeft = window.innerWidth - rect.width;
      var maxTop = window.innerHeight - rect.height;

      this.player.style.left = Math.max(0, Math.min(this.initialLeft + dx, maxLeft)) + 'px';
      this.player.style.top = Math.max(0, Math.min(this.initialTop + dy, maxTop)) + 'px';

      event.preventDefault();
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.player.classList.remove('is-dragging');

      if (!this.wasDragged) return;

      // Snap to whichever side of the screen the player is closest to.
      var rect = this.player.getBoundingClientRect();
      var center = rect.left + rect.width / 2;
      var snapLeft =
        center < window.innerWidth / 2 ? EDGE_MARGIN : window.innerWidth - rect.width - EDGE_MARGIN;

      this.player.classList.add('is-snapping');
      this.player.style.left = snapLeft + 'px';

      setTimeout(
        function () {
          this.player.classList.remove('is-snapping');
        }.bind(this),
        450
      );
    }
  };

  if (!window.customElements.get('pdp-floating-video')) {
    window.customElements.define('pdp-floating-video', PdpFloatingVideo);
  }
})();
